import { useMemo, useState } from "react";
import { NAV_ITEMS, ROLE_LABELS, roleDefaultPages } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Plus, Minus, Download, Grid3x3 } from "lucide-react";

const PAGES = NAV_ITEMS.filter((i) => i.to !== "/");

const cellState = (u, roleDoc, to) => {
  const def = roleDefaultPages(roleDoc, u.role).includes(to);
  if (u.role === "admin") return "on";
  if (!Array.isArray(u.page_access)) return def ? "on" : "off";
  const has = u.page_access.includes(to);
  return has ? (def ? "on" : "extra") : def ? "removed" : "off";
};

const Cell = ({ s }) => (
  <td className={`border-l border-border text-center ${s === "extra" ? "bg-emerald-500/15" : s === "removed" ? "bg-red-500/10" : ""}`}>
    {s === "on" && <Check className="mx-auto h-4 w-4 text-primary" />}
    {s === "extra" && <Plus className="mx-auto h-4 w-4 text-emerald-600" />}
    {s === "removed" && <Minus className="mx-auto h-4 w-4 text-red-600" />}
  </td>
);

const exportCsv = (rows, roles, props) => {
  const label = { on: "Yes", extra: "Yes (extra)", removed: "No (removed)", off: "" };
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["Name", "Email", "Role", "Properties", "Custom", ...PAGES.map((p) => p.label)];
  const lines = rows.map((u) => {
    const rd = roles.find((r) => r.key === u.role);
    const pn = (u.property_ids || []).map((id) => props.find((p) => p.id === id)?.name).filter(Boolean).join("; ");
    return [u.name, u.email, rd?.label || ROLE_LABELS[u.role] || u.role, pn, Array.isArray(u.page_access) ? "Yes" : "", ...PAGES.map((p) => label[cellState(u, rd, p.to)])];
  });
  const blob = new Blob([[head, ...lines].map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `access-summary-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
};

export const AccessSummary = ({ users, roles, props, onEdit }) => {
  const [role, setRole] = useState("all");
  const [prop, setProp] = useState("all");
  const rows = useMemo(() => users
    .filter((u) => u.active !== false && u.status !== "invited")
    .filter((u) => role === "all" || u.role === role)
    .filter((u) => prop === "all" || (u.property_ids || []).includes(prop) || ["admin", "regional", "finance", "hr"].includes(u.role))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "")), [users, role, prop]);
  return (
    <Card className="mt-8 rounded-none border" data-testid="access-summary">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Grid3x3 className="h-4 w-4" /> Access Summary</h2>
          <p className="text-xs text-muted-foreground">Every active user and the pages they can see. <Plus className="inline h-3 w-3 text-emerald-600" /> extra beyond role · <Minus className="inline h-3 w-3 text-red-600" /> removed from role. Click a user to edit.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-9 w-44" data-testid="access-filter-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={prop} onValueChange={setProp}>
            <SelectTrigger className="h-9 w-48" data-testid="access-filter-property"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All properties</SelectItem>
              {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => exportCsv(rows, roles, props)} data-testid="access-export"><Download className="mr-1.5 h-4 w-4" /> Excel (CSV)</Button>
        </div>
      </div>
      <div className="max-h-[70vh] overflow-auto">
        <table className="w-full text-xs" data-testid="access-table">
          <thead className="sticky top-0 z-10 bg-card">
            <tr className="border-b border-border">
              <th className="sticky left-0 z-20 min-w-[200px] bg-card px-3 py-2 text-left">User</th>
              {PAGES.map((p) => (
                <th key={p.to} className="h-36 w-9 border-l border-border align-bottom">
                  <div className="mx-auto flex w-6 items-center justify-start whitespace-nowrap pb-2 [writing-mode:vertical-rl] rotate-180 font-semibold">
                    {p.label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={PAGES.length + 1} className="p-6 text-center text-muted-foreground">No users.</td></tr>}
            {rows.map((u) => {
              const rd = roles.find((r) => r.key === u.role);
              const editable = u.role !== "admin";
              return (
                <tr key={u.id} onClick={() => editable && onEdit(u)} className={`border-b border-border transition-colors ${editable ? "cursor-pointer hover:bg-secondary/60" : ""}`} data-testid={`access-row-${u.id}`}>
                  <td className="sticky left-0 bg-card px-3 py-1.5">
                    <div className="font-semibold">{u.name}{Array.isArray(u.page_access) && <span className="ml-1 text-primary" title="Custom access">✱</span>}</div>
                    <div className="text-[0.65rem] text-muted-foreground">{rd?.label || ROLE_LABELS[u.role] || u.role}</div>
                  </td>
                  {PAGES.map((p) => <Cell key={p.to} s={cellState(u, rd, p.to)} />)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
